import { type FormEvent, useEffect, useState } from "react";
import type { AccountSetupStatus, ActivationPreview, TotpEnrollment } from "@kavoosh/contracts";
import { Check, KeyRound, ShieldCheck } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

import {
  acknowledgeSetupRecoveryCodes,
  beginSetupTotp,
  completeAccountSetup,
  confirmSetupTotp,
  getAccountSetupStatus,
  previewActivation,
  reissueSetupRecoveryCodes,
  setAccountSetupPassword,
  startActivation,
} from "./setupApi";
import { replaceNavigation } from "../../navigation";
import { useAuthCopy } from "./authCatalog";

type Stage = "loading" | "preview" | "setup" | "error";

function secretFrom(uri: string): string {
  try { return new URL(uri).searchParams.get("secret") ?? ""; }
  catch { return ""; }
}

export function AccountSetupPage({
  activationToken,
  onComplete,
  onReturnToLogin,
}: {
  activationToken?: string | undefined;
  onComplete: (workspaceHome: string) => void;
  onReturnToLogin: () => void;
}) {
  const { t } = useAuthCopy();
  const [stage, setStage] = useState<Stage>("loading");
  const [preview, setPreview] = useState<ActivationPreview | null>(null);
  const [status, setStatus] = useState<AccountSetupStatus | null>(null);
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const activationMode = activationToken !== undefined;
    document.title = activationMode
      ? t("setup.documentActivate")
      : t("setup.documentSetup");
    const load = activationMode
      ? previewActivation(activationToken).then((result) => {
        if (!active) return;
        setPreview(result);
        setStage("preview");
      })
      : getAccountSetupStatus().then((result) => {
        if (!active) return;
        setStatus(result);
        setStage("setup");
      });
    void load.catch((caught: unknown) => {
      if (!active) return;
      setError(caught instanceof Error ? caught.message : t("setup.unavailableBody"));
      setStage("error");
    });
    return () => { active = false; };
  }, [activationToken, t]);

  async function action<T>(operation: () => Promise<T>, apply: (value: T) => void | Promise<void>) {
    setBusy(true);
    setError(null);
    try { await apply(await operation()); }
    catch (caught) { setError(caught instanceof Error ? caught.message : t("setup.requestError")); }
    finally { setBusy(false); }
  }

  async function start() {
    if (activationToken === undefined) return;
    await action(() => startActivation(activationToken), (next) => {
      setStatus(next);
      setStage("setup");
      replaceNavigation("/setup");
    });
  }

  async function savePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const fields = new FormData(form);
    await action(() => setAccountSetupPassword({
      newPassword: String(fields.get("new_password") ?? ""),
    }), (next) => {
      setStatus(next);
      form.reset();
    });
  }

  async function beginTotp() {
    await action(beginSetupTotp, (next) => setEnrollment(next));
  }

  async function confirmTotp(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!enrollment) return;
    const code = String(new FormData(event.currentTarget).get("code") ?? "");
    await action(() => confirmSetupTotp(enrollment.factor_id, code), async (codes) => {
      setRecoveryCodes(codes);
      setEnrollment(null);
      setStatus(await getAccountSetupStatus());
    });
  }

  async function reissueCodes() {
    await action(reissueSetupRecoveryCodes, (codes) => setRecoveryCodes(codes));
  }

  async function acknowledgeCodes() {
    await action(acknowledgeSetupRecoveryCodes, (next) => setStatus(next));
  }

  async function finish() {
    await action(completeAccountSetup, (result) => onComplete(result.actor.workspace_home));
  }

  if (stage === "loading") {
    return <main className="setup-page"><section className="setup-card"><p>{t("setup.loading")}</p></section></main>;
  }
  if (stage === "error") {
    return <main className="setup-page"><section className="setup-card"><h1>{t("setup.unavailable")}</h1><p role="alert">{error}</p><button className="setup-secondary" onClick={onReturnToLogin}>{t("setup.return")}</button></section></main>;
  }

  if (stage === "preview" && preview) {
    return <main className="setup-page">
      <section className="setup-card setup-preview" aria-labelledby="activation-title">
        <span className="setup-icon"><ShieldCheck /></span>
        <p className="setup-eyebrow">{t("setup.invitationEyebrow")}</p>
        <h1 id="activation-title">{t("setup.activateTitle")}</h1>
        <p>{t("setup.invitationBody", { name: preview.display_name, email: preview.email, workspace: preview.workspace.replaceAll("_", " ") })}</p>
        <p className="setup-security-note">{t("setup.invitationSafety")}</p>
        {error && <p className="setup-error" role="alert">{error}</p>}
        <button className="setup-primary" disabled={busy} onClick={() => void start()}>{busy ? t("setup.starting") : t("setup.continue")}</button>
      </section>
    </main>;
  }

  if (!status) return null;

  if (status.origin === "bootstrap") {
    return <main className="setup-page">
      <section className="setup-card setup-preview" aria-labelledby="legacy-setup-title">
        <span className="setup-icon"><ShieldCheck /></span>
        <p className="setup-eyebrow">{t("setup.retiredEyebrow")}</p>
        <h1 id="legacy-setup-title">{t("setup.retiredTitle")}</h1>
        <p>{t("setup.retiredBody")}</p>
        <p className="setup-security-note">{t("setup.retiredSafety")}</p>
        <button className="setup-secondary" onClick={onReturnToLogin}>{t("setup.return")}</button>
      </section>
    </main>;
  }

  const emailReady = status.email_verified;
  const passwordReady = status.password_set;
  const totpReady = status.authenticator_configured;
  const codesReady = status.recovery_codes_acknowledged;

  if (!emailReady) {
    return <main className="setup-page">
      <section className="setup-card setup-preview" aria-labelledby="email-proof-title">
        <span className="setup-icon"><ShieldCheck /></span>
        <p className="setup-eyebrow">{t("setup.emailEyebrow")}</p>
        <h1 id="email-proof-title">{t("setup.emailTitle")}</h1>
        <p>{t("setup.emailBody")}</p>
        <p className="setup-security-note">{t("setup.emailSafety")}</p>
        <button className="setup-secondary" onClick={onReturnToLogin}>{t("setup.return")}</button>
      </section>
    </main>;
  }

  return <main className="setup-page">
    <section className="setup-shell" aria-labelledby="setup-title">
      <header><span className="setup-icon"><ShieldCheck /></span><div><p className="setup-eyebrow">{t("setup.securityEyebrow")}</p><h1 id="setup-title">{t("setup.securityTitle")}</h1><p>{t("setup.securityBody", { name: status.display_name, workspace: status.workspace.replaceAll("_", " ") })}</p></div></header>
      <ol className="setup-progress" aria-label={t("setup.progress")}>
        <li className={passwordReady ? "complete" : "current"}><Check />{t("setup.password")}</li>
        <li className="complete"><Check />{t("setup.email")}</li>
        <li className={totpReady ? "complete" : passwordReady ? "current" : ""}><Check />{t("setup.authenticator")}</li>
        <li className={codesReady ? "complete" : totpReady ? "current" : ""}><Check />{t("setup.recoveryCodes")}</li>
      </ol>
      {error && <p className="setup-error" role="alert">{error}</p>}

      {!passwordReady && <form className="setup-step" onSubmit={(event) => void savePassword(event)}>
        <div className="setup-step-heading"><KeyRound /><div><h2>{t("setup.createPassword")}</h2><p>{t("setup.passwordBody")}</p></div></div>
        <label>{t("setup.newPassword")}<input name="new_password" type="password" autoComplete="new-password" minLength={14} required /></label>
        <button className="setup-primary" disabled={busy}>{busy ? t("setup.saving") : t("setup.savePassword")}</button>
      </form>}

      {passwordReady && !totpReady && <section className="setup-step">
        <div className="setup-step-heading"><ShieldCheck /><div><h2>{t("setup.authenticatorTitle")}</h2><p>{t("setup.authenticatorBody")}</p></div></div>
        {!enrollment && <button className="setup-primary" disabled={busy} onClick={() => void beginTotp()}>{busy ? t("setup.preparing") : t("setup.authenticatorSetup")}</button>}
        {enrollment && <div className="setup-enrollment"><QRCodeSVG value={enrollment.provisioning_uri} size={184} title={t("setup.qrTitle")} /><div><p>{t("setup.qrBody")}</p><code>{secretFrom(enrollment.provisioning_uri)}</code><form onSubmit={(event) => void confirmTotp(event)}><label>{t("setup.sixDigit")}<input name="code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" required /></label><button className="setup-primary" disabled={busy}>{busy ? t("setup.verifying") : t("setup.verifyAuthenticator")}</button></form></div></div>}
      </section>}

      {totpReady && !codesReady && <section className="setup-step">
        <div className="setup-step-heading"><KeyRound /><div><h2>{t("setup.recoveryTitle")}</h2><p>{t("setup.recoveryBody")}</p></div></div>
        {recoveryCodes.length === 0 ? <div><p>{t("setup.recoveryMissing")}</p><button className="setup-secondary" disabled={busy} onClick={() => void reissueCodes()}>{busy ? t("setup.issuing") : t("setup.issueCodes")}</button></div> : <><ul className="setup-recovery-codes" aria-label={t("setup.recoveryAria")}>{recoveryCodes.map((code) => <li key={code}><code>{code}</code></li>)}</ul><p className="setup-security-note">{t("setup.recoverySafety")}</p><button className="setup-primary" disabled={busy} onClick={() => void acknowledgeCodes()}>{busy ? t("setup.saving") : t("setup.savedCodes")}</button></>}
      </section>}

      {passwordReady && totpReady && codesReady && <section className="setup-step">
        <div className="setup-step-heading"><Check /><div><h2>{t("setup.completeTitle")}</h2><p>{t("setup.completeBody")}</p></div></div>
        <button className="setup-primary" disabled={busy} onClick={() => void finish()}>{busy ? t("setup.finishing") : t("setup.enter")}</button>
      </section>}
    </section>
  </main>;
}
